import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../../../services/ApiService';
import ConfirmationModal from '../../../../../../components/ModalConfirmation';
import { emissorLabel, extractErrorMessage, normalizeDecimal } from '../constants';
import { TAX_CODES_VAZIO, TaxCodesData } from '../../taxCodes';
import { EmissorResumo, EnquadramentoIpiOption, NcmProfileDto, NcmProfileItemDto } from '../types';
import {
  AddRuleActions,
  CardActions,
  Field,
  FirstTaxSection,
  FieldGrid,
  MiniButton,
  Note,
  PrimaryButton,
  RuleCard,
  RuleCardTitle,
  SectionHead,
  SectionHint,
  TaxSection,
} from './style';

interface TaxCode {
  id?: number;
  code: string;
  description: string;
}

type TaxCodes = Pick<TaxCodesData, 'cstPisCofins' | 'cstIbsCbs' | 'cstIpi'>;

interface Props {
  item: NcmProfileItemDto;
  emissores: EmissorResumo[];
  onSaved: () => void;
  onFeedback: (message: string, ok: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
}

interface Draft {
  cstIPI: string;
  aliquotaIPI: string;
  cEnq: string;
  pisCofins: boolean;
  cstPIS_COFINS: string;
  aliquotaPIS: string;
  aliquotaCOFINS: string;
  ibsCbs: boolean;
  cstIBS_CBS: string;
  classificacaoTributaria: string;
}

const draftFrom = (p: NcmProfileDto | null): Draft => ({
  cstIPI: p?.cstIPI ?? '50',
  aliquotaIPI: p?.aliquotaIPI != null ? String(p.aliquotaIPI).replace('.', ',') : '',
  cEnq: p?.codigoEnquadramentoIPI ?? '',
  pisCofins: !!p?.cstPIS_COFINS,
  cstPIS_COFINS: p?.cstPIS_COFINS ?? '01',
  aliquotaPIS: p?.aliquotaPIS != null ? String(p.aliquotaPIS).replace('.', ',') : '',
  aliquotaCOFINS: p?.aliquotaCOFINS != null ? String(p.aliquotaCOFINS).replace('.', ',') : '',
  ibsCbs: !!p?.cstIBS_CBS,
  cstIBS_CBS: p?.cstIBS_CBS ?? '',
  classificacaoTributaria: p?.classificacaoTributaria ?? '',
});

interface RuleFormProps {
  ncmId: number;
  titulo: string;
  companyEmissorId: number | null;
  perfil: NcmProfileDto | null;
  taxCodes: TaxCodes;
  onSaved: (companyEmissorId: number | null) => void;
  onCancelNew?: () => void;
  onFeedback: (message: string, ok: boolean) => void;
  dirtyKey: string;
  onDirtyChange: (key: string, dirty: boolean) => void;
}

const mesmoDraft = (a: Draft, b: Draft): boolean =>
  (Object.keys(a) as (keyof Draft)[]).every((k) => a[k] === b[k]);

// Remontado por chave (id + updatedAt do perfil): recarregar a lista não apaga o que se digita aqui.
function RuleForm({
  ncmId,
  titulo,
  companyEmissorId,
  perfil,
  taxCodes,
  onSaved,
  onCancelNew,
  onFeedback,
  dirtyKey,
  onDirtyChange,
}: RuleFormProps): React.ReactElement {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(perfil));
  const sujo = useMemo(() => !mesmoDraft(draft, draftFrom(perfil)), [draft, perfil]);

  useEffect(() => {
    onDirtyChange(dirtyKey, sujo);
  }, [dirtyKey, sujo, onDirtyChange]);

  useEffect(() => () => onDirtyChange(dirtyKey, false), [dirtyKey, onDirtyChange]);
  const [enqOptions, setEnqOptions] = useState<EnquadramentoIpiOption[]>([]);
  const [classTribOptions, setClassTribOptions] = useState<TaxCode[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    let ativo = true;
    ApiService.fiscal
      .getEnquadramentosIpi(draft.cstIPI)
      .then((r) => {
        if (ativo) setEnqOptions(r.data ?? []);
      })
      .catch((error: unknown) => console.error('Erro ao buscar enquadramentos do IPI:', error));
    return () => {
      ativo = false;
    };
  }, [draft.cstIPI]);

  const cstIbsCbsId = useMemo(
    () => taxCodes.cstIbsCbs.find((c) => c.code === draft.cstIBS_CBS)?.id,
    [taxCodes.cstIbsCbs, draft.cstIBS_CBS],
  );

  useEffect(() => {
    if (!draft.ibsCbs || cstIbsCbsId === undefined) {
      setClassTribOptions([]);
      return;
    }
    let ativo = true;
    ApiService.fiscal
      .getClassTrib({ cstIbsCbsId })
      .then((r: { data?: TaxCode[] }) => {
        if (ativo) setClassTribOptions(r.data ?? []);
      })
      .catch((error: unknown) => console.error('Erro ao buscar cClassTrib:', error));
    return () => {
      ativo = false;
    };
  }, [draft.ibsCbs, cstIbsCbsId]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const cstIpiSaida = taxCodes.cstIpi.filter((c) => c.sentido === 'SAIDA');
  const cstIpiDe = (code: string) => cstIpiSaida.find((c) => c.code === code);
  const cstIpiAtual = cstIpiDe(draft.cstIPI);
  const ipiZerado = cstIpiAtual ? !cstIpiAtual.exigeAliquota : false;
  const pisCofinsZerado = Boolean(taxCodes.cstPisCofins.find((c) => c.code === draft.cstPIS_COFINS)?.aliquotaZero);
  const semEscolhaLabel = cstIpiAtual?.enquadramentoPadrao
    ? `Padrão pelo CST (${cstIpiAtual.enquadramentoPadrao})`
    : 'Selecione (obrigatório)';
  const cstForaDaLista = !cstIpiAtual;

  const onChangeCstIpi = (cst: string) =>
    setDraft((d) => ({ ...d, cstIPI: cst, aliquotaIPI: cstIpiDe(cst)?.exigeAliquota === false ? '0' : d.aliquotaIPI, cEnq: '' }));

  const onChangeCstPisCofins = (cst: string) =>
    setDraft((d) => ({
      ...d,
      cstPIS_COFINS: cst,
      ...(taxCodes.cstPisCofins.find((c) => c.code === cst)?.aliquotaZero ? { aliquotaPIS: '0', aliquotaCOFINS: '0' } : {}),
    }));

  const salvar = async () => {
    if (cstIpiAtual && cstIpiAtual.enquadramentoPadrao === null && !draft.cEnq) {
      onFeedback(`Escolha o enquadramento (cEnq) do CST IPI ${cstIpiAtual.code}.`, false);
      return;
    }
    const aliquotaDigitada = draft.aliquotaIPI.trim();
    const aliquotaIPI = ipiZerado
      ? '0'
      : aliquotaDigitada !== ''
        ? normalizeDecimal(aliquotaDigitada)
        : cstIpiAtual?.exigeAliquota
          ? undefined
          : '0';
    setSaving(true);
    try {
      await ApiService.fiscal.upsertNcmProfile({
        ncmId,
        companyEmissorId,
        cstIPI: draft.cstIPI,
        aliquotaIPI,
        codigoEnquadramentoIPI: draft.cEnq || null,
        cstPIS_COFINS: draft.pisCofins ? draft.cstPIS_COFINS : null,
        aliquotaPIS: draft.pisCofins && draft.aliquotaPIS.trim() ? normalizeDecimal(draft.aliquotaPIS) : null,
        aliquotaCOFINS: draft.pisCofins && draft.aliquotaCOFINS.trim() ? normalizeDecimal(draft.aliquotaCOFINS) : null,
        cstIBS_CBS: draft.ibsCbs && draft.cstIBS_CBS ? draft.cstIBS_CBS : null,
        classificacaoTributaria: draft.ibsCbs && draft.classificacaoTributaria ? draft.classificacaoTributaria : null,
      });
      onFeedback(`Regra de ${titulo.toLowerCase()} salva.`, true);
      onSaved(companyEmissorId);
    } catch (error: unknown) {
      onFeedback(extractErrorMessage(error, 'Erro ao salvar a regra.'), false);
    } finally {
      setSaving(false);
    }
  };

  const remover = async () => {
    if (!perfil) return;
    setConfirmRemove(false);
    try {
      await ApiService.fiscal.deleteNcmProfile(perfil.id);
      onFeedback(`Regra de ${titulo.toLowerCase()} removida.`, true);
      onSaved(companyEmissorId);
    } catch (error: unknown) {
      onFeedback(extractErrorMessage(error, 'Erro ao remover a regra.'), false);
    }
  };

  return (
    <RuleCard>
      <RuleCardTitle>{titulo}</RuleCardTitle>

      <FirstTaxSection>
        <SectionHead as="div">IPI</SectionHead>
        <FieldGrid>
          <Field $full>
            <label>CST</label>
            <select value={draft.cstIPI} onChange={(e) => onChangeCstIpi(e.target.value)}>
              {cstForaDaLista && <option value={draft.cstIPI}>{draft.cstIPI} · gravado na regra</option>}
              {cstIpiSaida.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.code} · {o.description}
                </option>
              ))}
            </select>
          </Field>
          <Field>
            <label>Alíquota %</label>
            <input
              inputMode="decimal"
              placeholder="0,00"
              value={ipiZerado ? '0' : draft.aliquotaIPI}
              disabled={ipiZerado}
              onChange={(e) => set('aliquotaIPI', e.target.value)}
            />
          </Field>
          <Field>
            <label>cEnq</label>
            <select value={draft.cEnq} onChange={(e) => set('cEnq', e.target.value)}>
              <option value="">{semEscolhaLabel}</option>
              {enqOptions.map((o) => (
                <option key={o.code} value={o.code} title={o.description}>
                  {o.code} · {o.description.length > 60 ? `${o.description.slice(0, 60)}…` : o.description}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>
      </FirstTaxSection>

      <TaxSection>
        <SectionHead>
          <input type="checkbox" checked={draft.pisCofins} onChange={(e) => set('pisCofins', e.target.checked)} />
          PIS/COFINS
          <SectionHint>{draft.pisCofins ? 'personalizado para este NCM' : 'da natureza de operação'}</SectionHint>
        </SectionHead>
        {draft.pisCofins && (
          <FieldGrid>
            <Field $full>
              <label>CST</label>
              <select value={draft.cstPIS_COFINS} onChange={(e) => onChangeCstPisCofins(e.target.value)}>
                {taxCodes.cstPisCofins.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} · {o.description}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              <label>Alíquota PIS %</label>
              <input
                inputMode="decimal"
                placeholder="0,00"
                value={pisCofinsZerado ? '0' : draft.aliquotaPIS}
                disabled={pisCofinsZerado}
                onChange={(e) => set('aliquotaPIS', e.target.value)}
              />
            </Field>
            <Field>
              <label>Alíquota COFINS %</label>
              <input
                inputMode="decimal"
                placeholder="0,00"
                value={pisCofinsZerado ? '0' : draft.aliquotaCOFINS}
                disabled={pisCofinsZerado}
                onChange={(e) => set('aliquotaCOFINS', e.target.value)}
              />
            </Field>
          </FieldGrid>
        )}
      </TaxSection>

      <TaxSection>
        <SectionHead>
          <input type="checkbox" checked={draft.ibsCbs} onChange={(e) => set('ibsCbs', e.target.checked)} />
          IBS/CBS
          <SectionHint>{draft.ibsCbs ? 'personalizado para este NCM' : 'do cenário fiscal'}</SectionHint>
        </SectionHead>
        {draft.ibsCbs && (
          <FieldGrid>
            <Field $full>
              <label>CST</label>
              <select
                value={draft.cstIBS_CBS}
                onChange={(e) => setDraft((d) => ({ ...d, cstIBS_CBS: e.target.value, classificacaoTributaria: '' }))}
              >
                <option value="">Selecione</option>
                {taxCodes.cstIbsCbs.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} · {o.description}
                  </option>
                ))}
              </select>
            </Field>
            <Field $full>
              <label>cClassTrib</label>
              <select
                value={draft.classificacaoTributaria}
                disabled={!draft.cstIBS_CBS}
                onChange={(e) => set('classificacaoTributaria', e.target.value)}
              >
                <option value="">Selecione</option>
                {classTribOptions.map((o) => (
                  <option key={o.code} value={o.code} title={o.description}>
                    {o.code} · {o.description.length > 60 ? `${o.description.slice(0, 60)}…` : o.description}
                  </option>
                ))}
              </select>
            </Field>
          </FieldGrid>
        )}
      </TaxSection>

      <Note>ICMS vem sempre do cenário fiscal.</Note>

      <CardActions>
        {perfil && (
          <MiniButton type="button" $danger onClick={() => setConfirmRemove(true)}>
            Remover regra
          </MiniButton>
        )}
        {!perfil && onCancelNew && (
          <MiniButton type="button" onClick={onCancelNew}>
            Cancelar
          </MiniButton>
        )}
        <PrimaryButton type="button" disabled={saving} onClick={salvar}>
          {saving ? 'Salvando…' : 'Salvar regra'}
        </PrimaryButton>
      </CardActions>

      <ConfirmationModal
        isOpen={confirmRemove}
        title="Remover regra?"
        message={`A regra de ${titulo.toLowerCase()} deixa de valer. Sem outra regra, o NCM volta a bloquear a venda.`}
        actions={[
          { label: 'Cancelar', onClick: () => setConfirmRemove(false), variant: 'outline' },
          { label: 'Remover regra', onClick: remover, variant: 'danger' },
        ]}
      />
    </RuleCard>
  );
}

export default function RuleTab({ item, emissores, onSaved, onFeedback, onDirtyChange }: Props): React.ReactElement {
  const [taxCodes, setTaxCodes] = useState<TaxCodes>(TAX_CODES_VAZIO);
  const [novos, setNovos] = useState<(number | null)[]>([]);
  const [sujos, setSujos] = useState<Set<string>>(new Set());

  const marcarSujo = useCallback((key: string, dirty: boolean) => {
    setSujos((prev) => {
      if (prev.has(key) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  useEffect(() => {
    onDirtyChange(sujos.size > 0);
  }, [sujos, onDirtyChange]);

  useEffect(() => {
    let ativo = true;
    ApiService.fiscal
      .getTaxCodes()
      .then((r: { data?: Partial<TaxCodes> }) => {
        if (ativo)
          setTaxCodes({ cstPisCofins: r.data?.cstPisCofins ?? [], cstIbsCbs: r.data?.cstIbsCbs ?? [], cstIpi: r.data?.cstIpi ?? [] });
      })
      .catch((error: unknown) => console.error('Erro ao buscar códigos tributários:', error));
    return () => {
      ativo = false;
    };
  }, []);

  const perfis = useMemo(
    () => [...item.perfis].sort((a, b) => (a.companyEmissorId ?? -1) - (b.companyEmissorId ?? -1)),
    [item.perfis],
  );
  const emissorPorId = useMemo(() => new Map(emissores.map((e) => [e.companyId, e])), [emissores]);
  const tituloDe = (companyEmissorId: number | null) => {
    if (companyEmissorId === null) return 'Todos os emissores';
    const e = emissorPorId.get(companyEmissorId);
    return e ? emissorLabel(e) : `Emissor ${companyEmissorId}`;
  };

  const comRegra = new Set(perfis.map((p) => p.companyEmissorId));
  // Sem nenhuma regra, o cartão de todos os emissores fica aberto até ser salvo.
  const pendentes = perfis.length === 0 && !novos.includes(null) ? [null, ...novos] : novos;
  const semRegra = emissores.filter((e) => !comRegra.has(e.companyId) && !novos.includes(e.companyId));
  const temRegraGeral = comRegra.has(null) || pendentes.includes(null);

  const aoSalvar = (companyEmissorId: number | null) => {
    setNovos((n) => n.filter((x) => x !== companyEmissorId));
    onSaved();
  };

  return (
    <>
      {perfis.map((p) => (
        <RuleForm
          key={`${p.id}-${p.updatedAt}`}
          ncmId={item.ncmId}
          titulo={tituloDe(p.companyEmissorId)}
          companyEmissorId={p.companyEmissorId}
          perfil={p}
          taxCodes={taxCodes}
          onSaved={aoSalvar}
          onFeedback={onFeedback}
          dirtyKey={`${p.id}-${p.updatedAt}`}
          onDirtyChange={marcarSujo}
        />
      ))}
      {pendentes.map((id) => (
        <RuleForm
          key={`novo-${id ?? 'todos'}`}
          ncmId={item.ncmId}
          titulo={tituloDe(id)}
          companyEmissorId={id}
          perfil={null}
          taxCodes={taxCodes}
          onSaved={aoSalvar}
          onCancelNew={novos.includes(id) ? () => setNovos((n) => n.filter((x) => x !== id)) : undefined}
          onFeedback={onFeedback}
          dirtyKey={`novo-${id ?? 'todos'}`}
          onDirtyChange={marcarSujo}
        />
      ))}
      <AddRuleActions>
        {!temRegraGeral && (
          <MiniButton type="button" onClick={() => setNovos((n) => [...n, null])}>
            + Regra para todos os emissores
          </MiniButton>
        )}
        {semRegra.map((e) => (
          <MiniButton key={e.companyId} type="button" onClick={() => setNovos((n) => [...n, e.companyId])}>
            + Regra para {emissorLabel(e)}
          </MiniButton>
        ))}
      </AddRuleActions>
    </>
  );
}
